import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import apiClient from "../api/apiClient";
import { message } from "antd";

export interface Order {
  id: number;
  user_id: number;
  waiter: string;
  additional_information?: string;
  order_status_id: number;
  payment_status_id: number;
  location_id: number;
  reference: string;
  is_internal_order: number;
  start_date: string;
  end_date?: string | null;
  estimated_end_date?: string | null;
  is_archived: number;
  is_approved: number;
  is_express: number;
  created_at: string;
  updated_at: string;
  deleted_at?: string | null;

  user: {
    id: number;
    name: string;
    email: string;
    phone: string;
    user_type?: {
      id: number;
      name: string;
      description?: string;
      icon?: string;
    };
    location?: {
      id: number;
      name: string;
      address?: string;
      code?: string;
    };
  };

  order_status: {
    id: number;
    name: string;
  };

  order_items: {
    id: number;
    order_id: number;
    product_id: number;
    qty: number;
    amount: string;
    product: {
      id: number;
      name: string;
      amount: string;
      discount?: string;
      category?: {
        id: number;
        name: string;
      };
      vendor?: {
        id: number;
        name: string;
        email: string;
        phone: string;
        avatar?: string;
      };
      image?: {
        id: number;
        url: string;
      };
    };
  }[];
}

export interface OrderStatus {
  id: number;
  name: string;
}

// ✅ Custom function to create a stable query key
const createQueryKey = (page: number, filters: Record<string, any>) => {
  return ["orders", page, JSON.stringify(filters)];
};

export const useOrders = (
  page: number,
  filters: Record<string, any> = {},
  includeUnassigned: boolean = false
) => {
  return useQuery({
    queryKey: createQueryKey(page, { ...filters, includeUnassigned }),
    queryFn: async () => {
      // Helper function to filter out invalid values
      const filterValidValues = (obj: Record<string, any>) => {
        return Object.fromEntries(
          Object.entries(obj).filter(
            ([_, v]) =>
              v !== null &&
              v !== "" &&
              v !== undefined &&
              !(Array.isArray(v) && v.length === 0)
          )
        );
      };

      if (includeUnassigned && filters.waiter) {
        // Call 1: Get orders assigned to the staff member
        const assignedParams = new URLSearchParams({
          page: page.toString(),
          ...filterValidValues(filters),
        }).toString();

        // Call 2: Get unassigned orders (no waiter filter)
        const unassignedFilters = { ...filters };
        delete unassignedFilters.waiter;

        const unassignedParams = new URLSearchParams({
          page: page.toString(),
          ...filterValidValues(unassignedFilters),
        }).toString();

        // Make both API calls
        const [assignedResponse, unassignedResponse] = await Promise.all([
          apiClient.get<{
            data: Order[];
            meta: { total: number; current_page: number; last_page: number };
          }>(`/order?${assignedParams}`),
          apiClient.get<{
            data: Order[];
            meta: { total: number; current_page: number; last_page: number };
          }>(`/order?${unassignedParams}`),
        ]);

        // Filter unassigned orders to only include those without a waiter
        const unassignedOrders = unassignedResponse.data.data.filter(
          (order) => !order.waiter || order.waiter.trim() === ""
        );

        // Merge the results, avoiding duplicates
        const assignedOrders = assignedResponse.data.data;
        const mergedOrders = [...assignedOrders];

        // Add unassigned orders that aren't already in the assigned list
        unassignedOrders.forEach((unassignedOrder) => {
          if (!mergedOrders.find((order) => order.id === unassignedOrder.id)) {
            mergedOrders.push(unassignedOrder);
          }
        });

        // Sort by created_at descending
        mergedOrders.sort(
          (a, b) =>
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );

        return {
          data: mergedOrders,
          meta: {
            ...assignedResponse.data.meta,
            total: mergedOrders.length,
          },
        };
      }

      // Standard single API call for admin users or when not including unassigned
      const queryParams = new URLSearchParams({
        page: page.toString(),
        ...filterValidValues(filters),
      }).toString();

      const response = await apiClient.get<{
        data: Order[];
        meta: { total: number; current_page: number; last_page: number };
      }>(`/order?${queryParams}`);

      return response.data;
    },
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchInterval: 20 * 1000,
    refetchIntervalInBackground: false,
    enabled: !!page,
  });
};

export const useOrderStatuses = () => {
  return useQuery({
    queryKey: ["orderStatuses"],
    queryFn: async () => {
      const response = await apiClient.get<{ data: OrderStatus[] }>(
        "/orderstatus"
      );
      return response.data.data;
    },
    staleTime: 1000 * 60 * 60, // ✅ Cache for 1 hour (statuses don't change often)
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
};

export const useOrderDetails = (orderId: number) => {
  return useQuery({
    queryKey: ["orderDetails", orderId],
    queryFn: async () => {
      const response = await apiClient.get<{ data: Order }>(
        `/order/${orderId}`
      );
      return response.data.data;
    },
    staleTime: 0, // ✅ Always consider data stale for real-time updates
    refetchOnWindowFocus: true, // ✅ Refetch when user comes back to the page
    refetchInterval: 20 * 1000, // ✅ Refetch every 20 seconds
    refetchIntervalInBackground: false, // ✅ Only refetch when tab is active
    enabled: !!orderId, // ✅ Fetch only when orderId exists
  });
};

export const UpdateOrderStatus = async (orderId: number, statusId: number) => {
  const response = await apiClient.put(`/order/${orderId}`, {
    order_status_id: statusId,
  });
  return response.data;
};

export const useAssignWaiter = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      orderId,
      waiterName,
    }: {
      orderId: number;
      waiterName: string | null;
    }) => {
      const response = await apiClient.put(`/order/${orderId}`, {
        waiter: waiterName,
      });
      return response.data;
    },
    onSuccess: () => {
      message.success("Waiter assigned successfully");
      queryClient.invalidateQueries({ queryKey: ["orderDetails"] });
      queryClient.invalidateQueries({ queryKey: ["orders"] });
    },
    onError: (error: any) => {
      console.error("Failed to assign waiter:", error);
      message.error("Failed to assign waiter");
    },
  });
};
